import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {SpeechModel} from 'app/blocks/service/speech/speech-model.interface';
import {RealtimeClient} from '@speechmatics/real-time-client';

@Injectable({
    providedIn: 'root'
})
export class SpeechmaticsModelService implements SpeechModel {
    private client: RealtimeClient;
    private isConnected = false;
    private transcriptionText = '';
    private transcriptionSubject = new BehaviorSubject<string>('');
    private mediaRecorder: MediaRecorder | null = null;
    private endOfTranscriptPromise: Promise<void> | null = null;
    private endOfTranscriptResolve: (() => void) | null = null;
    private _isRecording = false;
    private audioChunks: Blob[] = [];

    transcription$ = this.transcriptionSubject.asObservable();
    get isRecording(): boolean {
        return this._isRecording;
    }

    constructor() {
        this.client = new RealtimeClient();
        this.setupClientListeners();
    }

    private setupClientListeners() {
        this.client.addEventListener('receiveMessage', ({data}) => {
            if (data.message === 'AddTranscript') {
                const text = data.results.map((r) => r.alternatives?.[0].content).join(' ');
                this.transcriptionText = this.transcriptionText + ' ' + text;
                this.transcriptionSubject.next(this.transcriptionText);
            } else if (data.message === 'EndOfTranscript') {
                this.isConnected = false;
                if (this.endOfTranscriptResolve) {
                    this.endOfTranscriptResolve();
                }
            }
        });
    }

    async startRecording(): Promise<void> {
        try {
            const jwt = await this.fetchJWT('zz3wcbe56A27BSo16cjFD7Cbi3PuGrHn');
            await this.client.start(jwt, {
                transcription_config: {
                    language: 'en',
                    diarization: 'none',
                    operating_point: 'enhanced',
                    max_delay_mode: 'flexible',
                    max_delay: 1,
                    enable_partials: false,
                    enable_entities: false
                }
            });
            this.isConnected = true;
            this.transcriptionText = '';
            this.transcriptionSubject.next(this.transcriptionText);
            await this.setupMicrophone();
            this._isRecording = true;
        } catch (error) {
            console.error('Error starting Speechmatics recording:', error);
            throw error;
        }
    }

    private async setupMicrophone() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 2,
                    sampleRate: 96000
                }
            });

            this.mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 96000
            });

            this.mediaRecorder.addEventListener('dataavailable', (event) => {
                if (this.isConnected && event.data.size > 0) {
                    this.client.sendAudio(event.data);
                    this.audioChunks.push(event.data);
                }
            });

            this.mediaRecorder.addEventListener('error', (error) => {
                console.error('MediaRecorder error:', error);
            });

            this.mediaRecorder.start(100);
        } catch (error) {
            console.error('Error setting up microphone:', error);
            throw error;
        }
    }

    private async fetchJWT(apiKey: string): Promise<string> {
        const resp = await fetch('https://mp.speechmatics.com/v1/api_keys?type=rt', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${apiKey}`
            },
            body: JSON.stringify({
                ttl: 3600
            })
        });

        if (!resp.ok) {
            throw new Error(`Bad response from API: ${resp.status}`);
        }

        return (await resp.json()).key_value;
    }

    async stopRecording(): Promise<string> {
        this._isRecording = false;

        if (this.mediaRecorder) {
            this.mediaRecorder.stop();
            this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());
            this.mediaRecorder = null;
        }
        if (this.isConnected) {
            this.endOfTranscriptPromise = new Promise((resolve) => {
                this.endOfTranscriptResolve = resolve;
            });
            this.client.stopRecognition();
            this.isConnected = false;
            await this.endOfTranscriptPromise;
            this.endOfTranscriptPromise = null;
            this.endOfTranscriptResolve = null;
        }
        return this.transcriptionText;
    }

    async getAudioData(): Promise<Blob> {
        if (this.audioChunks.length === 0) {
            return null;
        }
        const audioBlob = new Blob(this.audioChunks, {type: 'audio/webm;codecs=opus'});
        this.audioChunks = [];
        return audioBlob;
    }
}
